import {Detail, Icon, Link, Pill, PillVariant, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';

export interface BasedOnItem {
	variableName: string;
}

export interface BasedOnListProps {
	items: BasedOnItem[];
	maxVisible?: number;
}

const DEFAULT_MAX_VISIBLE = 5;

// Optional chaining for the same reason NovaInvocationControl needs it: this module loads
// transitively through the shared web-components barrel, and a test that mocks
// '@forecast-it/design-system' without providing tokensForThemeProvider would otherwise crash on
// import alone, before any test ran.
const sys = tokensForThemeProvider?.sky?.sys?.color;
const size = tokensForThemeProvider?.sky?.sys?.size;

// The panel is the design system's Frame, read as the values Frame itself renders rather than
// mounted as the component: Frame takes only the props it knows and drops a className or a
// data-cy, and this panel is what the suite selects on. Raised with the design-system team
// alongside Pill, ColouredContainer and SplitButton.Section, which all drop them the same way.
//
// It used to be its own blue panel. Inside a Nova card that is now a Frame too, a second tinted
// box read as a separate thing rather than a footnote on the answer above it.
const PANEL_SURFACE = sys?.background.neutral['2'] ?? '';
const PANEL_BORDER = sys?.border.decorative ?? '';
const PANEL_RADIUS = size?.borderradius['3'] ?? '';
const PANEL_PADDING = tokensForThemeProvider?.sky?.sys?.spacing?.['100'] ?? '4px';

// A bounded panel rather than a bare list: the sources are a footnote on the answer, and an edge
// around them is what stops them reading as more of what the agent said.
const Panel = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 4px;
	padding: ${PANEL_PADDING};
	background: ${PANEL_SURFACE};
	border: 1px solid ${PANEL_BORDER};
	border-radius: ${PANEL_RADIUS};
`;

// The label and its icon, in the design system's own auxiliary tone now that the panel underneath
// them is neutral rather than tinted.
const PanelLabel = styled.div`
	display: inline-flex;
	align-items: center;
	gap: 3px;
	flex: none;
`;

const Chips = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 4px;
	flex: 1;
	min-width: 0;
`;

// Each source is a neutral design-system Pill. The note this replaces said Pill's outlined variant
// carries a status dot and there is no status to report - true, and the filled neutral variant it
// uses instead carries none.
// Pinned to the same compact fit the agent tag is, and for the same reason: Pill's one size is
// built for a page header, and these are footnote labels under an answer. See NovaLabelChip.
const ChipSlot = styled.span`
	display: inline-flex;
	min-width: 0;

	> * {
		height: 20px;
		min-height: 20px;
		padding: 0 6px;
		box-sizing: border-box;
	}

	> * * {
		font-size: 11px;
		line-height: 16px;
	}
`;

export const BasedOnList = (props: BasedOnListProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const [expanded, setExpanded] = useState(false);
	const maxVisible = props.maxVisible ?? DEFAULT_MAX_VISIBLE;

	if (props.items.length === 0) {
		return (
			<Text size="2" data-cy="nova-based-on-empty">
				{formatMessage({id: 'nova_invocation.based_on.no_variables_recorded'})}
			</Text>
		);
	}

	const visible = expanded ? props.items : props.items.slice(0, maxVisible);
	const hiddenCount = props.items.length - visible.length;

	return (
		<div data-cy="nova-based-on-list">
			<Panel>
				<PanelLabel>
					{/* aria-hidden: the label beside it already says what this panel is. */}
					<Icon icon="search" size="s" color="auxiliary" aria-hidden="true" />
					<Detail color="medium">{formatMessage({id: 'nova_invocation.based_on.label'})}</Detail>
				</PanelLabel>
				{/* role="list" holds only role="listitem" children — the label beside it and the "show
				    more" link below sit outside it, as this wrapper's own siblings, not inside it. */}
				<Chips role="list" aria-label={formatMessage({id: 'nova_invocation.based_on.label'})}>
					{visible.map((item, index) => (
						// Index-qualified: BasedOnItem doesn't guarantee variableName is unique, and a
						// duplicate would otherwise collide as a React key and mis-reconcile rows.
						// The chip is the row; nothing wraps it. A wrapper left the chip inline-level
						// inside a block, which put it on a line box of its own - baseline-aligned, with
						// the strut's descender space reserved under it - so it rode above the line
						// "Based on" sits on. A direct child of a flex container is blockified instead,
						// and there is no line box left to sit on.
						<ChipSlot role="listitem" key={`${index}-${item.variableName}`}>
							<Pill variant={PillVariant.NEUTRAL}>{item.variableName}</Pill>
						</ChipSlot>
					))}
				</Chips>
			</Panel>
			{hiddenCount > 0 && (
				// Link, not Button — this is an inline "reveal more of this list" affordance sitting
				// among plain text rows, not an action that warrants button chrome. Link supports
				// exactly this (no href, click-only) via its own documented default.
				<Link data-cy="nova-based-on-show-more" onClick={() => setExpanded(true)}>
					{formatMessage({id: 'nova_invocation.based_on.show_more'}, {count: hiddenCount})}
				</Link>
			)}
		</div>
	);
};
